/*
 * This code is the intellectual property of Bulb Inc. and other software publishers;
 * it may not be altered, copied or disclosed without prior written approval.
 * Copyright 2021, Bulb Inc.  For more information, please email contact@hellobulb.com.
 *
 *                _
 *  _            | | _
 * | |         _ | || |
 * | |__   _  | || || |__
 * | '_ \ | | | || || '_ \
 * | (_) || |_| || || (_) |
 *  \___/  \___/ |_| \___/
 *
 */

/**
 * @module image/image/imageediting
 */

import { ViewDowncastWriter, Editor, Plugin } from 'ckeditor5';
import type { ModelElement, ViewElement, ModelWriter } from 'ckeditor5';
import BulbImageLoadObserver from './bulbimageloadobserver';
import { viewFigureToModel, modelToViewAttributeConverter } from './converters';

import { toImageWidget } from './bulbimageutils';

import { InsertBulbImageCommand } from './bulbimagecommand';
import { BulbImageCropCommand } from './bulb-imagecrop/bulbimagecropcommand';
import { BulbImageRotateCommand } from './bulb-imagerotate/bulbimagerotatecommand';
import { getBlockVersion } from '../utils';

/**
 * The image editor plugin.
 *
 * It registers:
 *
 * * `<image>` as a block element in the model schema, and allows `alt`, `src` and `srcset` attributes.
 * * converters for editing and data pipelines.
 * * `'imageInsert'` command.
 *
 * @extends Plugin
 */
export default class BulbImageEditing extends Plugin {

  /**
   * @inheritDoc
   */
  public static get pluginName() {
    return 'BulbImageEditing' as const;
  }

  /**
   * @inheritDoc
   */
  public init() : void {
    const editor = this.editor as Editor;
    const { schema } = editor.model;
    const { conversion } = editor;

    // See https://github.com/ckeditor/ckeditor5-image/issues/142.
    editor.editing.view.addObserver(BulbImageLoadObserver);

    // Configure schema.
    schema.register('image', {
      isObject: true,
      isBlock: true,
      allowWhere: '$block',
      allowAttributes: ['src', 'bulb-block-id', 'blockAlignment', 'width', 'bulb-block-version'],
    });

    conversion.for('dataDowncast').elementToElement({
      model: 'image',
      view: (modelElement, { writer: viewWriter }) => viewWriter.createContainerElement('figure', {
        class: 'image',
        width: String(modelElement.getAttribute('width')),
        'bulb-block-id': String(modelElement.getAttribute('bulb-block-id')),
        'bulb-block-version': getBlockVersion(CKEBulbData, modelElement),
        src: this.getImageSrc(this.getImageAsset(CKEBulbData.page, modelElement)),
      }),
    });

    conversion.for('editingDowncast').elementToElement({
      model: 'image',
      view: (
        modelElement,
        { writer: viewWriter },
      ) => toImageWidget(this.createImageViewElement(modelElement, viewWriter), viewWriter),
    });

    conversion.for('downcast')
      .add(modelToViewAttributeConverter('src'));

    conversion.for('upcast')
      .elementToElement({
        view: {
          name: 'figure',
          classes: 'image',
        },
        model: (viewImage, { writer: modelWriter }) => this.createImageModelElement(viewImage, modelWriter),
      })
      .add(viewFigureToModel);

    // Register our various image-related commands here.
    editor.commands.add('insertBulbImage', new InsertBulbImageCommand(editor));
    editor.commands.add('rotateBulbImage', new BulbImageRotateCommand(editor));
    editor.commands.add('cropBulbImage', new BulbImageCropCommand(editor));
  }

  /**
   * Creates a view element representing the image utilizing the bulb-image component.
   *
   * <figure class="image"><bulb-image>...</bulb-image></figure>
   *
   * Note that `alt` and `src` attributes are converted separately, so they are not included.
   *
   * @param modelElement model element to be written to the view.
   * @param writer : ViewDowncastWriter to use in writing to the ckeditor view.
   * @returns The figure element representing the image.
   */
  public createImageViewElement(modelElement: ModelElement, writer: ViewDowncastWriter) {
    // const that = this;
    // const { editor } = that;

    let figure = null;
    const figureAttribute = {
      class: 'image no-editor-placeholder',
      width: modelElement.getAttribute('width'),
      'bulb-block-id': modelElement.getAttribute('bulb-block-id'),
      'bulb-block-version': modelElement.getAttribute('bulb-block-version'),
      src: null,
    };

    if (CKEBulbData.page.unit && CKEBulbData.page.unit.contentBlocks) {
      const contentBlock = CKEBulbData.page.unit.contentBlocks[modelElement.getAttribute('bulb-block-id') as string];

      if (contentBlock) {
        const imageAsset = contentBlock.scaledImageAsset || contentBlock.fullImageAsset;

        figureAttribute.src = imageAsset.url;
        figureAttribute['bulb-block-version'] = contentBlock.version;
        figure = writer.createContainerElement('figure', figureAttribute);

        const uiElement = writer.createUIElement('div', {}, function (this, domDocument) {
          const domElement = this.toDomElement(domDocument);
          CKEBulbData.service.componentInjector.injectCkeImage(
            {
              imageAsset,
              imageId: modelElement.getAttribute('bulb-block-id'),
              isEditingPage: CKEBulbData.page.editing,
            },
            domElement,
            contentBlock.id,
          );
          return domElement;
        });

        const linkHref = modelElement.getAttribute('linkHref');
        if (linkHref) {
          const linkElement = writer.createContainerElement('a', { href: linkHref });
          writer.insert(writer.createPositionAt(figure, 'end'), linkElement);
          writer.insert(writer.createPositionAt(linkElement, 'end'), uiElement);
        } else {
          writer.insert(writer.createPositionAt(figure, 'end'), uiElement);
        }
      }
    } else {
      figure = writer.createContainerElement('figure', figureAttribute);
    }

    return figure;
  }

  /**
   * Create model element for image.
   *
   * @param viewImage View Image
   * @param modelWriter Model ModelWriter
   * @return {*}
   */
  createImageModelElement(viewImage: ViewElement, modelWriter: ModelWriter): ModelElement {
    // const editor = this.editor;
    let contentBlockVersion;
    if (CKEBulbData.page.unit && CKEBulbData.page.unit.contentBlocks) {
      const contentBlock = CKEBulbData.page.unit.contentBlocks[viewImage.getAttribute('bulb-block-id')];
      if (contentBlock) {
        contentBlockVersion = contentBlock.version;
      }
    }
    const linkElement = Array.from(viewImage.getChildren())
      .find((child) => child.is('element', 'a')) as ViewElement | undefined;
    const linkHref = linkElement?.getAttribute('href');

    return modelWriter.createElement('image', {
      'bulb-block-id': viewImage.getAttribute('bulb-block-id'),
      'bulb-block-version': contentBlockVersion || viewImage.getAttribute('bulb-block-version'),
      ...(linkHref ? { linkHref } : {}),
    });
  }

  /**
   * Get an image asset.
   *
   * @param page Page data
   * @param modelElement Model element
   * @returns {ImageAsset|*|null}
   */
  getImageAsset(page, modelElement) {
    if (page) {
      if (Object.keys(page).length === 0) {
        return null;
      }

      if (page.unit && page.unit.contentBlocks) {
        const contentBlock = page.unit.contentBlocks[modelElement.getAttribute('bulb-block-id')];

        if (contentBlock) {
          return contentBlock.scaledImageAsset || contentBlock.fullImageAsset;
        }
      }
    }
    return null;
  }

  /**
   * Get image asset url.
   *
   * @param imageAsset Asset of image.
   * @returns {null|*}
   */
  getImageSrc(imageAsset) {
    if (imageAsset) {
      return imageAsset.url;
    }
    return null;
  }
}
